import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../lib/auth';
import { staffApi, placementsApi, credentialsApi, onboardingApi, candidatesApi } from '../lib/api';
import AIActionPanel from '../components/AI/AIActionPanel';
import DashboardCustomizeModal from '../components/DashboardCustomizeModal';
import TodayFocusWidget from '../components/TodayFocusWidget';
import WorkforceStatusWidget from '../components/WorkforceStatusWidget';
import { readWidgetPrefs, WidgetId } from '../lib/dashboardPrefs';
// Phase 2 design system
import {
  AuroraPage, PageHeader, Button, Icon, StatTile, Badge, DonutChart, ProgressMeter,
} from '../components/ui';
import type { IconName } from '../components/ui';

function relativeTimeAgo(ms: number): string {
  if (!ms) return 'never';
  const diff = Math.floor((Date.now() - ms) / 1000);
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

// Owns its own 10s tick so the "updated Xs ago" label stays fresh WITHOUT
// re-rendering the whole dashboard (charts, tiles, KPI computations) every 10s.
function LastUpdated({ at }: { at: number }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const h = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(h);
  }, []);
  return <>updated {relativeTimeAgo(at)}</>;
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function Dashboard() {
  const { user } = useUser();
  const navigate = useNavigate();
  const firstName = user?.firstName ?? 'there';

  // Widget visibility prefs — user-customizable, persisted per-browser.
  // Read once on mount + on save; re-render when prefs change.
  const [widgets, setWidgets] = useState<Record<WidgetId, boolean>>(() => readWidgetPrefs(user?.id ?? null));
  const [customizeOpen, setCustomizeOpen] = useState(false);
  useEffect(() => { setWidgets(readWidgetPrefs(user?.id ?? null)); }, [user?.id]);
  const show = useMemo(() => (id: WidgetId) => widgets[id] !== false, [widgets]);
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const { data: staffData, dataUpdatedAt: staffUpdatedAt } = useQuery({
    queryKey: ['staff-active'],
    queryFn: () => staffApi.list({ status: 'active' }),
    refetchInterval: 60000,
  });

  const { data: pendingData, dataUpdatedAt: pendingUpdatedAt } = useQuery({
    queryKey: ['placements-pending'],
    queryFn: () => placementsApi.list({ status: 'pending' }),
    refetchInterval: 60000,
  });

  // Added so "Active Placements" KPI matches the Placements page exactly.
  const { data: activePlacementsData } = useQuery({
    queryKey: ['placements-active'],
    queryFn: () => placementsApi.list({ status: 'active' }),
    refetchInterval: 60000,
  });

  // CHANGED: previously counted staff.status='onboarding' which was
  // populated separately from the Onboarding page (which pulls candidates
  // by stage). Switched to the same data source the Onboarding page uses
  // so the two always agree.
  const { data: onbCandConfirmed } = useQuery({
    queryKey: ['onboarding-candidates-confirmed'],
    queryFn: () => candidatesApi.list({ stage: 'confirmed' }),
    refetchInterval: 60000,
  });
  const { data: onbCandLegacy, dataUpdatedAt: onboardingUpdatedAt } = useQuery({
    queryKey: ['onboarding-candidates-legacy'],
    queryFn: () => candidatesApi.list({ stage: 'onboarding' }),
    refetchInterval: 60000,
  });

  const { data: expiringData, dataUpdatedAt: expiringUpdatedAt } = useQuery({
    queryKey: ['credentials-expiring'],
    queryFn: () => credentialsApi.expiring(),
    refetchInterval: 60000,
  });

  const { data: onboardingData } = useQuery({
    queryKey: ['onboarding-summary'],
    queryFn: () => onboardingApi.summary(),
    refetchInterval: 60000,
  });
  void onboardingData; // fetched to warm cache / reserved for future widgets

  // Use the most-recent fetch for the header "updated" indicator
  const lastUpdated = Math.max(staffUpdatedAt ?? 0, pendingUpdatedAt ?? 0, onboardingUpdatedAt ?? 0, expiringUpdatedAt ?? 0);

  const activeEmployees = staffData?.data?.staff?.length ?? 0;
  const pendingPlacements = pendingData?.data?.placements?.length ?? 0;
  const activePlacements = activePlacementsData?.data?.placements?.length ?? 0;
  // In Onboarding = candidates in stages 'confirmed' + 'onboarding'
  // (matches the Onboarding page exactly)
  const onboardingCount =
    (onbCandConfirmed?.data?.candidates?.length ?? 0) +
    (onbCandLegacy?.data?.candidates?.length ?? 0);
  const expiringSoon = expiringData?.data?.expiringSoon ?? [];
  const alreadyExpired = expiringData?.data?.alreadyExpired ?? [];
  const expiringCreds = expiringSoon.length;
  const expiredCreds = alreadyExpired.length;
  const expiringWithin7 = expiringSoon.filter((c) => {
    if (!c.expiry_date) return false;
    const days = Math.ceil((new Date(c.expiry_date).getTime() - Date.now()) / 86400000);
    return days <= 7;
  }).length;
  const totalCredIssues = expiredCreds + expiringCreds;
  // If no staff AND no credentials at all, compliance rate is genuinely
  // unknown — show '—' instead of a confusing 0% or misleading 100%.
  const hasAnyCredData = activeEmployees > 0 || totalCredIssues > 0;
  const complianceRate: number | null = !hasAnyCredData
    ? null
    : totalCredIssues === 0
      ? 100
      : Math.max(0, Math.round(100 - (totalCredIssues / Math.max(1, activeEmployees)) * 20));
  const complianceRateDisplay = complianceRate == null ? '—' : `${complianceRate}%`;
  const complianceAccent = complianceRate == null
    ? 'var(--t3)'
    : complianceRate >= 90 ? 'var(--good)' : complianceRate >= 70 ? 'var(--warn)' : 'var(--danger)';
  const criticalAlert = expiredCreds > 0 || expiringCreds >= 3;

  const quickLinks: { icon: IconName; label: string; nav: string }[] = [
    { icon: 'candidates', label: 'Candidates', nav: '/candidates' },
    { icon: 'clipboard', label: 'Credentialing', nav: '/credentialing' },
    { icon: 'onboarding', label: 'Onboarding', nav: '/onboarding' },
    { icon: 'placement', label: 'Placements', nav: '/placements' },
    { icon: 'bell', label: 'Reminders', nav: '/reminders' },
    { icon: 'chart', label: 'Reports', nav: '/reports' },
  ];

  return (
    <AuroraPage>
      {/* Header */}
      <PageHeader
        title={<>{getGreeting()}, {firstName}</>}
        subtitle={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{today}</span>
            <Badge tone="good" dot title="Live data — refreshes every 60 seconds">
              Live · <LastUpdated at={lastUpdated} />
            </Badge>
          </span>
        }
        actions={
          <>
            <Button variant="secondary" size="sm" icon={<Icon name="settings" size={15} />} onClick={() => setCustomizeOpen(true)} title="Customize which widgets appear on this dashboard">
              Customize
            </Button>
            <Button variant="secondary" size="sm" icon={<Icon name="sparkles" size={15} />} onClick={() => navigate('/ai-assistant')}>
              Ask AI
            </Button>
            <Button size="sm" icon={<Icon name="plus" size={15} />} onClick={() => navigate('/staff')}>
              Add Staff
            </Button>
          </>
        }
      />

      {/* Phase 6.6 — Context-aware AI suggestions for the dashboard.
          Toggleable via Customize. */}
      {show('ai_suggestions') && <AIActionPanel
        subject="Today's priorities for the FNS operator"
        context={{
          active_employees: activeEmployees,
          pending_placements: pendingPlacements,
          active_placements: activePlacements,
          in_onboarding: onboardingCount,
          credentials_expiring_30d: expiringCreds,
          credentials_expired: expiredCreds,
        }}
      />}

      {/* Critical alert banner — customizable but auto-hides if nothing critical anyway */}
      {show('critical_alert') && criticalAlert && (
        <div className="critical-banner">
          <Icon name="alert" size={20} />
          <div>
            <strong>Compliance Alert: </strong>
            {expiredCreds > 0 && `${expiredCreds} expired credential${expiredCreds > 1 ? 's' : ''} require immediate attention. `}
            {expiringCreds > 0 && `${expiringCreds} credential${expiringCreds > 1 ? 's' : ''} expiring within 30 days.`}
            <span className="cta-link" style={{ marginLeft: 8 }} onClick={() => navigate('/credentialing')}>
              View now →
            </span>
          </div>
        </div>
      )}

      {/* 4 KPI tiles — clickable, each routes to the filtered destination */}
      {show('stat_cards') && (
        <div className="ui-stat-grid" style={{ marginBottom: 'var(--sp-6)' }}>
          <StatTile
            label="Active Employees"
            value={activeEmployees}
            sub="Currently active staff"
            icon={<Icon name="users" />}
            accent="var(--pr)"
            onClick={() => navigate('/staff')}
            title="Open Staff"
          />
          <StatTile
            label="Pending Placements"
            value={pendingPlacements}
            sub="Awaiting confirmation"
            icon={<Icon name="clock" />}
            accent="var(--warn)"
            onClick={() => navigate('/placements')}
            title="Open Placements"
          />
          <StatTile
            label="In Onboarding"
            value={onboardingCount}
            sub="Completing onboarding"
            icon={<Icon name="onboarding" />}
            accent="var(--chart-5)"
            onClick={() => navigate('/onboarding')}
            title="Open Onboarding"
          />
          <StatTile
            label="Compliance Rate"
            value={complianceRateDisplay}
            sub={complianceRate == null ? 'No credential data yet' : 'Credential compliance'}
            icon={<Icon name="shield" />}
            accent={complianceAccent}
            onClick={() => navigate('/compliance/admin/records')}
            title="Open Compliance Records"
          />
        </div>
      )}

      {/* Row 1: Immediate Actions + Compliance Alerts — each independently toggleable */}
      {(show('immediate_actions') || show('compliance_alerts')) && <div className="grid-2">
        {/* Immediate Actions */}
        {show('immediate_actions') && <div className="pn">
          <div className="pnh">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="pulse" size={16} /> Immediate Actions</h3>
            <Badge tone="neutral">Live</Badge>
          </div>
          <div className="pnb" style={{ padding: '8px 18px' }}>
            {totalCredIssues === 0 && onboardingCount === 0 ? (
              <div className="empty-state" style={{ padding: '24px' }}>
                <div className="ui-empty__icon" style={{ margin: '0 auto 12px', background: 'var(--good-tint)', color: 'var(--good-fg)' }}><Icon name="check-circle" size={26} /></div>
                <h3>All caught up!</h3>
                <p>No immediate actions required.</p>
              </div>
            ) : (
              <>
                {expiredCreds > 0 && (
                  <div className="action-item">
                    <div className="action-dot red" />
                    <div className="action-content">
                      <div className="action-title">
                        {expiredCreds} Expired Credential{expiredCreds > 1 ? 's' : ''}
                      </div>
                      <div className="action-meta">Remove from placement until renewed</div>
                    </div>
                    <Button variant="danger" size="sm" onClick={() => navigate('/credentialing')}>Fix Now</Button>
                  </div>
                )}
                {expiringWithin7 > 0 && (
                  <div className="action-item">
                    <div className="action-dot red" />
                    <div className="action-content">
                      <div className="action-title">
                        {expiringWithin7} Credential{expiringWithin7 > 1 ? 's' : ''} Expiring This Week
                      </div>
                      <div className="action-meta">Expires within 7 days — urgent renewal needed</div>
                    </div>
                    <Button variant="danger" size="sm" onClick={() => navigate('/credentialing')}>Fix Now</Button>
                  </div>
                )}
                {expiringCreds > expiringWithin7 && (
                  <div className="action-item">
                    <div className="action-dot orange" />
                    <div className="action-content">
                      <div className="action-title">
                        {expiringCreds - expiringWithin7} Credential{expiringCreds - expiringWithin7 > 1 ? 's' : ''} Expiring Soon
                      </div>
                      <div className="action-meta">Within 30 days — notify staff to renew</div>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => navigate('/credentialing')}>Review</Button>
                  </div>
                )}
                {onboardingCount > 0 && (
                  <div className="action-item">
                    <div className="action-dot blue" />
                    <div className="action-content">
                      <div className="action-title">
                        {onboardingCount} Staff in Onboarding
                      </div>
                      <div className="action-meta">Complete onboarding to activate placement eligibility</div>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => navigate('/onboarding')}>View</Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>}

        {/* Compliance Alerts */}
        {show('compliance_alerts') && <div className="pn">
          <div className="pnh">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="shield" size={16} /> Compliance Alerts</h3>
            <Button variant="ghost" size="sm" iconRight={<Icon name="arrow-right" size={14} />} onClick={() => navigate('/credentialing')}>Manage</Button>
          </div>
          <div className="pnb" style={{ padding: '8px 18px' }}>
            {[
              { label: 'Expired Credentials', value: expiredCreds, tone: 'danger' as const },
              { label: 'Expiring Within 7 Days', value: expiringWithin7, tone: 'danger' as const },
              { label: 'Expiring Within 30 Days', value: expiringCreds, tone: 'warn' as const },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px', borderRadius: 'var(--r-md)',
                  background: item.value > 0 ? `var(--${item.tone}-tint)` : 'var(--sf3)',
                  marginBottom: 10,
                  border: `1px solid ${item.value > 0 ? `var(--${item.tone}-border)` : 'var(--bd)'}`,
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/credentialing')}
              >
                <span style={{ fontSize: 13, color: 'var(--t2)', fontWeight: 500 }}>{item.label}</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: item.value > 0 ? `var(--${item.tone}-fg)` : 'var(--good-fg)' }}>
                  {item.value}
                </span>
              </div>
            ))}
            <div style={{ textAlign: 'center', padding: 8, fontSize: 13, color: 'var(--pr)', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/credentialing')}>
              View all in Credentialing →
            </div>
          </div>
        </div>}
      </div>}

      {/* Today's Focus — personalized priority list (collapsible) */}
      <TodayFocusWidget />

      {/* Workforce Status tracker (collapsible) */}
      {show('workforce_overview') && <WorkforceStatusWidget />}

      {/* Row 2: Workforce Overview (chart-forward) + Quick Access */}
      {(show('workforce_overview') || show('quick_access')) && <div className="grid-2" style={{ marginTop: 20 }}>
        {show('workforce_overview') && <div className="pn">
          <div className="pnh">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="chart" size={16} /> Workforce Overview</h3>
          </div>
          <div className="pnb">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20, alignItems: 'center', marginBottom: 16 }}>
              <DonutChart
                segments={[
                  { label: 'Active', value: activePlacements },
                  { label: 'Pending', value: pendingPlacements },
                  { label: 'Onboarding', value: onboardingCount },
                ]}
                centerLabel="Placements"
                ariaLabel="Workforce distribution: active, pending, and onboarding"
              />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {complianceRate == null ? (
                  <div style={{ textAlign: 'center', color: 'var(--t3)', fontSize: 13, padding: 20 }}>
                    <Icon name="shield" size={24} /><div style={{ marginTop: 6 }}>No credential data yet</div>
                  </div>
                ) : (
                  <ProgressMeter variant="gauge" value={complianceRate} tone="auto" label="Compliance rate" ariaLabel="Compliance rate" />
                )}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { label: 'Active Employees', value: activeEmployees, accent: 'var(--good)', nav: '/staff' },
                { label: 'Credential Alerts', value: totalCredIssues, accent: totalCredIssues > 0 ? 'var(--danger)' : 'var(--good)', nav: '/credentialing' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  style={{ background: 'var(--sf3)', borderRadius: 'var(--r-md)', padding: '12px 14px', cursor: 'pointer', border: '1px solid var(--bd)', borderLeft: `3px solid ${stat.accent}` }}
                  onClick={() => navigate(stat.nav)}
                >
                  <div style={{ fontSize: 22, fontWeight: 700, color: stat.accent }}>{stat.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>}

        {/* Quick Links */}
        {show('quick_access') && <div className="pn">
          <div className="pnh">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="placement" size={16} /> Quick Access</h3>
          </div>
          <div className="pnb">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              {quickLinks.map((link) => (
                <button
                  key={link.label}
                  type="button"
                  className="ui-card ui-card--interactive"
                  style={{ background: 'var(--sf3)', padding: '16px 10px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                  onClick={() => navigate(link.nav)}
                >
                  <span style={{ display: 'inline-flex', width: 36, height: 36, borderRadius: 'var(--r-md)', background: 'var(--brand-tint)', color: 'var(--pr)', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name={link.icon} size={19} />
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--t2)' }}>{link.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>}
      </div>}

      {/* Customize modal */}
      <DashboardCustomizeModal
        userId={user?.id ?? null}
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        onSaved={(prefs) => setWidgets(prefs)}
      />
    </AuroraPage>
  );
}
